// PlanYear REST helpers.

import { api } from "@/api/client";


export interface PlanYearRow {
  plan_year_id: string;
  account_id: string;
  effective_start: string;
  effective_end: string;
  /** ``YYYY-YYYY`` label derived from the coverage window. */
  year_range?: string;
  /** Path of the year-range bucket inside Account Documents
   *  (e.g. ``/plan-years/2026-2027/``). */
  documents_folder_path?: string;
  open_enrollment_start: string | null;
  open_enrollment_end: string | null;
  is_current: boolean;
  is_archived: boolean;
  archived_at: string | null;
  archived_by_id: string | null;
  notes: string | null;
  notes_document_id: string | null;
  open_commitments: number;
  done_commitments: number;
  archived_commitments: number;
  created_at: string | null;
  updated_at: string | null;
}


export interface PlanYearListResponse {
  rows: PlanYearRow[];
  total: number;
}


export interface PlanYearCreateInput {
  effective_start: string;
  effective_end: string;
  open_enrollment_start?: string | null;
  open_enrollment_end?: string | null;
  is_current?: boolean;
  notes?: string | null;
}


export type PlanYearPatchInput = Partial<PlanYearCreateInput> & {
  notes_document_id?: string | null;
};


export function fetchPlanYearList(
  accountId: string,
  archived: "active" | "archived" | "all" = "all",
  opts?: { signal?: AbortSignal },
): Promise<PlanYearListResponse> {
  const qs = `?archived=${archived}`;
  return api.get<PlanYearListResponse>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/plan_years/${qs}`,
    opts,
  );
}


export function fetchPlanYear(
  accountId: string, planYearId: string,
  opts?: { signal?: AbortSignal },
): Promise<PlanYearRow> {
  return api.get<PlanYearRow>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`
    + `/plan_years/${encodeURIComponent(planYearId)}`,
    opts,
  );
}


export function createPlanYear(
  accountId: string, input: PlanYearCreateInput,
): Promise<PlanYearRow> {
  return api.post<PlanYearRow>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/plan_years/`,
    input,
  );
}


export function updatePlanYear(
  accountId: string, planYearId: string, input: PlanYearPatchInput,
): Promise<PlanYearRow> {
  return api.patch<PlanYearRow>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`
    + `/plan_years/${encodeURIComponent(planYearId)}`,
    input,
  );
}


export function archivePlanYear(
  accountId: string, planYearId: string,
): Promise<PlanYearRow> {
  return api.post<PlanYearRow>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`
    + `/plan_years/${encodeURIComponent(planYearId)}/archive`,
  );
}


export function restorePlanYear(
  accountId: string, planYearId: string,
): Promise<PlanYearRow> {
  return api.post<PlanYearRow>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`
    + `/plan_years/${encodeURIComponent(planYearId)}/restore`,
  );
}
